import React, {
  createContext,
  FC,
  useState,
  useEffect,
  useContext,
} from 'react';
import { Template } from '../domain/entities/template';
import { useSearchParams } from 'react-router-dom';
import { useTemplates } from './TemplatesContext';

export type PreviewTemplate = Template | null;

export interface PreviewTemplateContextValue {
  previewTemplate: PreviewTemplate;
  setPreviewTemplate: (t: PreviewTemplate) => void;
  isSavedTemplate: boolean;
  setIsSavedTemplate: (isSaved: boolean) => void;
}

const PreviewTemplateContext = createContext<PreviewTemplateContextValue>({
  previewTemplate: null,
  setPreviewTemplate: () => {},
  isSavedTemplate: false,
  setIsSavedTemplate: () => {},
});

export const PreviewTemplateContextProvider: FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [isSavedTemplate, setIsSavedTemplate] = useState(false);
  const { templates } = useTemplates();
  const [searchParams] = useSearchParams();
  const savedTemplateSlug = searchParams.get('savedTemplateSlug') ?? '';

  useEffect(() => {
    if (savedTemplateSlug) {
      const savedTemplate = templates.find(
        (temp) => temp.slug === savedTemplateSlug,
      );
      setPreviewTemplate(savedTemplate || null);
      setIsSavedTemplate(true);
    }
  }, []);

  return (
    <PreviewTemplateContext.Provider
      value={{
        previewTemplate,
        setPreviewTemplate,
        isSavedTemplate,
        setIsSavedTemplate,
      }}
    >
      {children}
    </PreviewTemplateContext.Provider>
  );
};

export const usePreviewTemplate = () => {
  const context = useContext(PreviewTemplateContext);
  if (!context) {
    throw new Error(
      'usePreviewTemplate must be used within a PreviewTemplateContext',
    );
  }
  return context;
};
